import {indexBy, omit} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useLazyGetAdditionalNoteQuery, useSetAdditionalNoteMutation} from '@dms/api/sales';
import {
  useLazyGetCustomFieldsPayloadQuery,
  useLazyGetFieldDefinitionListQuery,
  useUpdateCustomFieldsPayloadMutation,
} from '@dms/api/settings';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useCloneSaleVehicleMutation,
  useCreateVehicleMutation,
  useLazyGetSaleVehicleAlbumPhotoListQuery,
  useLazyGetSaleVehicleQuery,
  useLazyGetVehicleQuery,
  useLazyListSaleVehicleAlbumsByVehicleQuery,
} from '@dms/api/vehicle';
import {
  SERVICE_INTERVALS_DEFAULT_VALUES,
  toAPIVehicle,
  toBaseVehicle,
  useGetCarFeatures,
} from '@dms/features/vehicle-forms-kit';

export function useHeadlessCloneVehicle() {
  const [getCarFeatures] = useGetCarFeatures();
  const [getVehicle] = useLazyGetVehicleQuery();
  const [getSaleVehicle] = useLazyGetSaleVehicleQuery();
  const [listAlbumsByVehicle] = useLazyListSaleVehicleAlbumsByVehicleQuery();
  const [getAlbumPhotoList] = useLazyGetSaleVehicleAlbumPhotoListQuery();
  const [getAdditionalNote] = useLazyGetAdditionalNoteQuery();
  const [getCustomFieldsPayload] = useLazyGetCustomFieldsPayloadQuery();
  const [getFieldDefinitionList] = useLazyGetFieldDefinitionListQuery();

  const [createVehicle] = useCreateVehicleMutation();
  const [cloneSaleVehicle] = useCloneSaleVehicleMutation();
  const [setAdditionalNote] = useSetAdditionalNoteMutation();
  const [updateCustomFieldsPayload] = useUpdateCustomFieldsPayloadMutation();

  const cloneVehicle = async (sourceVehicleId: string): Promise<string> => {
    const [
      vehicleData,
      saleVehicleData,
      albums,
      additionalNote,
      customFieldsPayload,
      fieldDefinitions,
    ] = await Promise.all([
      getVehicle({vehicleId: sourceVehicleId}).unwrap(),
      getSaleVehicle({vehicleId: sourceVehicleId}).unwrap(),
      listAlbumsByVehicle({vehicleId: sourceVehicleId}).unwrap(),
      getAdditionalNote({vehicleId: sourceVehicleId})
        .unwrap()
        .catch(() => null),
      getCustomFieldsPayload({
        recordId: sourceVehicleId,
        resourceId: EntityResourceIds.vehicle,
      })
        .unwrap()
        .catch(() => null),
      getFieldDefinitionList({
        type: 'tenant',
        resourceId: EntityResourceIds.vehicle,
      })
        .unwrap()
        .catch(() => null),
    ]);

    const baseVehicle = toBaseVehicle(omit(['registrationPlate', 'vin'], vehicleData));

    if (baseVehicle == null) {
      throw new Error(`Failed to build base vehicle model for vehicle id: ${sourceVehicleId}`);
    }

    const enrichedVehicleData = {
      ...baseVehicle,
      serviceIntervals: {
        ...SERVICE_INTERVALS_DEFAULT_VALUES,
        ...vehicleData.serviceIntervals,
      },
    };

    const activeAlbum = albums.find((album) => album.active && album.name === 'PHOTOS_CAR_SALES');

    const [albumPhotos, sourcingData] = await Promise.all([
      activeAlbum != null
        ? getAlbumPhotoList({
            saleVehicleId: activeAlbum.saleVehicleId,
            saleVehicleAlbumId: activeAlbum.id,
          }).unwrap()
        : Promise.resolve([]),
      getCarFeatures(enrichedVehicleData),
    ]);

    const apiPayload = toAPIVehicle(enrichedVehicleData, null, sourcingData);

    const createdVehicle = await createVehicle({
      createVehicleRequestBody: apiPayload,
      validateMandatoryFields: false,
    }).unwrap();

    const newVehicleId = createdVehicle.id;

    const cloneSaleVehiclePromise = cloneSaleVehicle({
      vehicleId: sourceVehicleId,
      cloneSaleVehicleRequestBody: {
        toVehicleId: newVehicleId,
        salePresetId: saleVehicleData.salePresetId,
        vehicleSourceCodeId: saleVehicleData.vehicleSourceCodeId,
        photos: albumPhotos.map((photo) => ({fileId: photo.fileId})),
        targetAlbum: albumPhotos.length > 0 ? 'PHOTOS_CAR_SALES' : null,
      },
    }).unwrap();

    const setAdditionalNotePromise =
      additionalNote != null && !isNilOrEmpty(additionalNote.note)
        ? setAdditionalNote({
            vehicleId: newVehicleId,
            additionalNoteRequestBody: {note: additionalNote.note},
          })
            .unwrap()
            .catch(() => null)
        : Promise.resolve();

    const updateCustomFieldsPromise = (() => {
      if (customFieldsPayload == null || fieldDefinitions == null) {
        return Promise.resolve();
      }

      const definitionsById = indexBy((item) => item.id, fieldDefinitions);
      const fieldsToClone = customFieldsPayload.filter((field) => {
        const definition = definitionsById[field.definitionId];
        return definition?.valueType === field.value.type;
      });

      return updateCustomFieldsPayload({
        recordId: newVehicleId,
        resourceId: EntityResourceIds.vehicle,
        fieldValues: fieldsToClone,
      })
        .unwrap()
        .catch(() => undefined);
    })();

    await Promise.all([
      cloneSaleVehiclePromise,
      setAdditionalNotePromise,
      updateCustomFieldsPromise,
    ]);

    // missing condition data, battery certificate, and omiting attach to business case from CloneSaleVehicle.tsx

    return newVehicleId;
  };

  return {cloneVehicle};
}
